'use client';

import { useTranslations } from 'next-intl';
import { analytics } from 'lib/analytics/analytics';
import { ShippingBox } from 'assets/icons';
import {
  MidPanel,
  MidPanelPhone,
  LeftPanel,
  RightPanel,
} from 'components/shared/BottomBar';
import React, { useContext } from 'react';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useModal } from '..';
import styles from './BottomBar.module.css';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { RareSelectionContext } from './RareSelectionContext';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { productMap_rare } from 'content/products/rare';
import { RarePriceData } from 'content/products/rare/productMap/constants';

const StyledPrimaryButton = ({
  className,
  ...props
}: React.ComponentProps<typeof PrimaryButton>) => (
  <PrimaryButton
    className={`${styles.styledPrimaryButton}${
      className ? ` ${className}` : ''
    }`}
    {...props}
  />
);

export const BottomBar = () => {
  const t = useTranslations('ring');
  const RareSelectionClient = useContext(RareSelectionContext);
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, taxString: s.taxString })),
  );
  const ModalClient = useModal();
  const formatPriceWithComma = useFormatPrice();

  const handleCheckout = () => {
    if (RareSelectionClient.size) {
      RareSelectionClient.size
        ? analytics.track(`Rare Bottom bar addto cart - CLICK`)
        : analytics.track(`Rare Bottom bar select color-size - CLICK`);
    }
    openCheckoutInfo();
  };

  const openCheckoutInfo = () => {
    handleProceedClick();
    return;
  };

  const handleProceedClick = () => {
    analytics.track('rare-checkout', {
      clicked: true,
    });
    RareSelectionClient.checkout();
    ModalClient.hide();
  };

  const sctollToOrderSummary = () => {
    const ref = document.getElementById('order-summary-card');
    if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const {
    size,
    shippingEstimate,
    color,
    defaultCurrency: currency,
    notServicable,
    goToNextStep,
    affiliateDiscount,
    effectiveDiscount,
    price,
  } = RareSelectionClient;

  const { store } = CartClient;

  // Get tax inclusivity from RarePriceData based on current store and color
  const getRareTaxString = () => {
    if (!store || !color) return CartClient.taxString;

    const priceData = RarePriceData[store as ShopifyStore]?.[color];
    if (!priceData) return CartClient.taxString;

    return priceData.tax_incl ? 'Tax incl' : 'Tax excl';
  };

  const rareTaxString = getRareTaxString();

  const MidPriceSection = (
    <>
      <MidPanel>
        <p className="total-price-single">
          {/* Show original price with strikethrough if there's an affiliate discount */}
          {affiliateDiscount > 0 && effectiveDiscount.type === 'affiliate' && (
            <span
              style={{
                textDecoration: 'line-through',
                color: '#999',
                marginRight: '8px',
              }}
            >
              {RareSelectionClient.currencySymbol}
              {formatPriceWithComma(price)}
            </span>
          )}
          {RareSelectionClient.currencySymbol}
          {formatPriceWithComma(RareSelectionClient.effectivePrice)}
          <span className="tax-disclaimer">({rareTaxString})</span>
        </p>
      </MidPanel>

      <MidPanelPhone onClick={sctollToOrderSummary}>
        <div className="subtitle-text-btn">Total</div>
        <p className="total-price-single">
          {/* Show original price with strikethrough if there's an affiliate discount */}
          {affiliateDiscount > 0 && effectiveDiscount.type === 'affiliate' && (
            <span
              style={{
                textDecoration: 'line-through',
                color: '#999',
                fontSize: '0.9em',
              }}
            >
              {RareSelectionClient.currencySymbol}
              {formatPriceWithComma(price)}
            </span>
          )}
          {RareSelectionClient.currencySymbol}
          {formatPriceWithComma(RareSelectionClient.effectivePrice)}
          <span className="tax-disclaimer">({rareTaxString})</span>
        </p>
      </MidPanelPhone>
    </>
  );

  if (notServicable === true) return null;

  return (
    <div className={`${styles.root} u-grid`}>
      <LeftPanel style={{ gridColumn: '1 / 6' }}>
        <p className="chat-specialist">
          <ShippingBox /> {shippingEstimate}
        </p>
      </LeftPanel>

      {size ? MidPriceSection : null}

      <RightPanel>
        {size ? (
          <StyledPrimaryButton
            onClick={handleCheckout}
            disabled={!size}
            data-buttontype="add to cart"
            data-variantId={
              color && size && currency
                ? productMap_rare[color]?.[size]?.variantId[
                    store as ShopifyStore
                  ]
                : ''
            }
            data-product="Ultrahuman Rare"
          >
            {t('ring.productSelection.cartSummary.addToCart')}
          </StyledPrimaryButton>
        ) : (
          <StyledPrimaryButton
            onClick={goToNextStep}
            data-buttontype="go to next step"
            data-variantId=""
            data-product="Ultrahuman Rare"
          >
            {t('ring.CONTINUE')}
          </StyledPrimaryButton>
        )}
      </RightPanel>
    </div>
  );
};
